// Context links are interactive only inside a chapter the reader is reading:
// `DocumentBlocks` also renders the admin workbench and diff panels, which
// have no provider and so keep rendering linked phrases as plain runs.

import { createContext, useMemo, type ReactNode } from "react";
import type { ReferenceLookupPorts } from "./reference-lookup";
import { ReferenceLookupHost } from "./reference-lookup-host";
import { readerReferencePorts } from "./reference-ports";

export interface ContextLinkScopeValue {
  bookSlug: string;
  chapterNumber: number;
}

export const ContextLinkScopeContext =
  createContext<ContextLinkScopeValue | null>(null);

export const CONTEXT_LINK_HINT_ID = "context-link-hint";

export function ContextLinkScope({
  bookSlug,
  chapterNumber,
  language = "en",
  ports = readerReferencePorts,
  children,
}: ContextLinkScopeValue & {
  /** The retelling's language, which every link's lookup is in. */
  language?: string;
  ports?: ReferenceLookupPorts;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ bookSlug, chapterNumber }),
    [bookSlug, chapterNumber],
  );
  return (
    <ContextLinkScopeContext value={value}>
      <ReferenceLookupHost scope={value} language={language} ports={ports}>
        {children}
      </ReferenceLookupHost>
      <span id={CONTEXT_LINK_HINT_ID} hidden>
        Opens a short background note.
      </span>
    </ContextLinkScopeContext>
  );
}
